import {
  AppointmentLinkRecordTypeSchema,
  type AppointmentLink,
  type AppointmentLinkRecordType,
  type SearchEntityType,
  type WorkspaceRoutes,
} from '@immo/api-contract';

// The ONE mapping between the workspace search's tokens and the link record types.
// `dokument` has no counterpart: a Dokument is not a linkable record.
const SEARCH_TO_RECORD = {
  objekt: 'PROPERTY',
  kontakt: 'CONTACT',
  deal: 'DEAL',
  lead: 'LEAD',
} as const satisfies Partial<Record<SearchEntityType, AppointmentLinkRecordType>>;

export type LinkableSearchType = keyof typeof SEARCH_TO_RECORD;

export const LINKABLE_SEARCH_TYPES: ReadonlyArray<LinkableSearchType> = ['objekt', 'kontakt', 'deal', 'lead'];

export const recordTypeOfSearchType = (entityType: SearchEntityType): AppointmentLinkRecordType | null =>
  entityType in SEARCH_TO_RECORD ? SEARCH_TO_RECORD[entityType as LinkableSearchType] : null;

// The order the detail panel lists links in: the Objekt is the likeliest jump.
export const LINK_KIND_ORDER: ReadonlyArray<AppointmentLinkRecordType> = [
  'PROPERTY',
  'CONTACT',
  'DEAL',
  'LEAD',
  'PROSPECT',
];

export const linkKey = (link: Pick<AppointmentLink, 'recordType' | 'recordId'>): string =>
  `${link.recordType}:${link.recordId}`;

export function linkTarget(routes: WorkspaceRoutes, link: Pick<AppointmentLink, 'recordType' | 'recordId'>): string {
  switch (link.recordType) {
    case 'PROPERTY':
      return routes.property(link.recordId);
    case 'CONTACT':
      return routes.contact(link.recordId);
    case 'DEAL':
      return routes.deal(link.recordId);
    case 'LEAD':
      return routes.lead(link.recordId);
    case 'PROSPECT':
      return routes.salesPipelineProspect(link.recordId);
  }
}

export type CreateFrom = AppointmentLink;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// `?createFrom=<TYPE>:<id>&label=<text>`. The label is transient display text for the
// chip only; what is persisted is (recordType, recordId).
export function parseCreateFrom(params: URLSearchParams): CreateFrom | null {
  const token = params.get('createFrom');
  const label = params.get('label')?.trim() ?? '';

  if (token === null || label === '') return null;

  const [recordType, recordId, ...rest] = token.split(':');
  const parsedType = AppointmentLinkRecordTypeSchema.safeParse(recordType);

  if (!parsedType.success || recordId === undefined || rest.length > 0 || !UUID_PATTERN.test(recordId)) return null;

  return { recordType: parsedType.data, recordId, label };
}

export const createFromToken = (recordType: AppointmentLinkRecordType, recordId: string): string =>
  `${recordType}:${recordId}`;

export type OpenAppointment = { readonly appointmentId: string; readonly on: string };

const DAY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

// A real calendar day, not just a well-shaped string: 2026-02-31 must not roll over to March.
export function parseDay(value: string): Date | null {
  const match = DAY_PATTERN.exec(value);

  if (match === null) return null;

  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const parsed = new Date(year, month - 1, day);

  return parsed.getFullYear() === year && parsed.getMonth() === month - 1 && parsed.getDate() === day ? parsed : null;
}

// `?appointment=<id>&on=<YYYY-MM-DD>`: a record page's jump into one appointment.
export function parseOpenAppointment(params: URLSearchParams): OpenAppointment | null {
  const appointmentId = params.get('appointment');
  const on = params.get('on');

  if (appointmentId === null || on === null) return null;

  return UUID_PATTERN.test(appointmentId) && parseDay(on) !== null ? { appointmentId, on } : null;
}
